import { BasicModal, Button } from 'components/elements';
import { H1, P } from 'components/fontSystem';
import { LOGIN } from 'containers/Routes/paths';
import PropTypes from 'prop-types';
import TranslationsContext from 'providers/translationsProvider';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import bfbTheme from 'styles/b4b-theme';

/**
 * Modal component that displays registration errors, including username conflicts,
 * password validation failures, and phone number errors.
 */
const RegistrationErrorModal = ({
  errors = false,
  loginParams = '',
  onCancel,
  onOpenResetModal,
}) => {
  const { t } = useContext(TranslationsContext);
  const navigate = useNavigate();

  const isUsernameExistsError = errors?.username;

  const getErrorMessages = () => {
    if (!errors) return [];

    const messages = [];
    const passwordErrors = errors.errors || [];

    const passwordErrorMap = {
      password_entirely_numeric: t('This password is entirely numeric.'),
      password_too_common: t('This password is too common.'),
      password_too_short: t('This password is too short. It must contain at least 10 characters.'),
      password_too_weak: t('Password must contain at least 1 number.'),
      password_too_similar: t('The password is too similar to your name or email'),
    };

    passwordErrors.forEach(errorKey => {
      if (passwordErrorMap[errorKey]) {
        messages.push(passwordErrorMap[errorKey]);
      }
    });

    if (errors.mentor?.phone_number || errors.member?.phone_number) {
      messages.push(t('Enter a valid phone number.'));
    }

    return messages;
  };

  const errorMessages = getErrorMessages();

  return (
    <BasicModal
      headerBgColor={bfbTheme.error}
      title={
        <H1 $mb0 color="white">
          {t('Error...')}
        </H1>
      }
      onCancel={onCancel}
      open={!!errors}
      width={600}
      footer={
        isUsernameExistsError && (
          <>
            <Button size="large" type="primary" onClick={() => navigate(`${LOGIN}${loginParams}`)}>
              {t('LOGIN')}
            </Button>
            <Button size="large" type="primary" onClick={onOpenResetModal}>
              {t('RESET MY PASSWORD')}
            </Button>
          </>
        )
      }
    >
      {/* User already exists with that email */}
      {isUsernameExistsError && (
        <P $mb0>
          {t(
            "It appears you've previously registered this email with us. If it rings a bell, please login with this email instead, or we will help you reset your password."
          )}
        </P>
      )}

      {/* Other registration errors */}
      {!isUsernameExistsError &&
        errorMessages.length > 0 &&
        errorMessages.map(message => (
          <P key={message} $semibold $mb0>
            {message}
          </P>
        ))}

      {/* Unexpected errors */}
      {!isUsernameExistsError && errorMessages.length === 0 && (
        <P $semibold $mb0>
          {t('Oops! Something went wrong...')}
        </P>
      )}
    </BasicModal>
  );
};

RegistrationErrorModal.propTypes = {
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  loginParams: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onOpenResetModal: PropTypes.func.isRequired,
};

export default RegistrationErrorModal;
